import {forwardRef, ReactElement, useEffect, useRef, useState} from 'react'
import DatePicker, {CalendarContainer, registerLocale} from 'react-datepicker'
import {addYears, subYears, getDate, format} from 'date-fns'
import {enUS, pl} from 'date-fns/locale'
import {useTranslation} from 'react-i18next'
import SVGIcon from './SVGIcon'
import {ReactDatePickerCustomHeaderProps} from 'react-datepicker/dist/calendar'
import {IDatePickerMode} from '../types/common'

type YearMonthPickerProps = {
    language: string
    selectedDate: Date | undefined
    setSelectedDate: (date: Date) => void
    datePickerMinDate: Date | undefined
    datePickerMaxDate: Date | undefined
    onDateChangeCallback?: (date: Date | null) => void
    filterDate?: ((date: Date) => boolean) | undefined
    excludeDates?: Date[] | undefined
    className?: string | undefined
    withCurrentDate?: boolean
}

registerLocale('pl', pl)
registerLocale('en', enUS)

const YearMonthPicker = ({
    language,
    selectedDate,
    setSelectedDate,
    datePickerMinDate,
    datePickerMaxDate,
    onDateChangeCallback = (date: Date | null) => date,
    filterDate = () => true,
    excludeDates = [],
    className = 'datepickers-item-input',
    withCurrentDate = false,
}: YearMonthPickerProps) => {
    const {t} = useTranslation()
    const datepickerRef = useRef<DatePicker>(null)
    const [isMonthPicker, setIsMonthPicker] = useState<boolean>(false)
    const [isYearPicker, setIsYearPicker] = useState<boolean>(false)
    const [useCustomHeader, setUseCustomHeader] = useState<boolean>(true)
    const [dateMode, setDateMode] = useState<IDatePickerMode>('current')

    useEffect(() => {
        if (selectedDate) {
            setDateMode('forday')
        }
    }, [selectedDate])

    const handleDateChange = (date: Date | null): void => {
        if (date) {
            if (isYearPicker) {
                setIsMonthPicker(true)
                setIsYearPicker(false)
                setUseCustomHeader(true)
            } else if (isMonthPicker) {
                setIsMonthPicker(false)
                setIsYearPicker(false)
            } else {
                setSelectedDate(date)
                onDateChangeCallback(date)
            }
        }
    }

    const handleChangeMode = (mode: IDatePickerMode) => {
        setDateMode(mode)
    }

    const handleChangeCurrentDate = () => {
        setSelectedDate(null as unknown as Date)
        onDateChangeCallback(null)
        datepickerRef.current?.setOpen(false)
    }

    const renderDayContents = (day, date) => {
        if (format(date, 'yyyy-MM-dd') === format(datePickerMaxDate as Date, 'yyyy-MM-dd')) {
            return (
                <div className="flex react-datepicker__day--max">
                    <div className="day-max">{getDate(date)}</div>
                    <SVGIcon id={'small_dots'} className={''} width={4} height={4} fill={'#007ab3'} />
                </div>
            )
        } else {
            return getDate(date)
        }
    }

    const customHeader = ({
        date,
        changeYear,
        decreaseMonth,
        increaseMonth,
    }: ReactDatePickerCustomHeaderProps): ReactElement => (
        <>
            <div>
                <div className="react-datepicker__header">
                    <button
                        type="button"
                        className="react-datepicker__navigation react-datepicker__navigation--previous"
                        aria-label="Previous Month"
                        onClick={() => {
                            if (isMonthPicker) {
                                changeYear(subYears(date, 1).getFullYear())
                            } else {
                                decreaseMonth()
                            }
                        }}>
                        <SVGIcon
                            id={'calendar-arrow-left'}
                            className={'light-blue'}
                            width={24}
                            height={24}
                            fill={'#006192'}
                        />
                        <span className="react-datepicker__navigation-icon--previous">
                            {t('label.datepicker.previous-month')}
                        </span>
                    </button>
                    <h2 className="react-datepicker__current-month">
                        {!isMonthPicker && date.toLocaleString(language, {month: 'long'})}
                        {'\u00A0'}
                        {date.getFullYear()}
                    </h2>
                    <button
                        type="button"
                        className="react-datepicker__navigation react-datepicker__navigation--next"
                        aria-label="Next Month"
                        onClick={() => {
                            if (isMonthPicker) {
                                changeYear(addYears(date, 1).getFullYear())
                            } else {
                                increaseMonth()
                            }
                        }}>
                        <SVGIcon
                            id={'calendar-arrow-right'}
                            className={'light-blue'}
                            width={24}
                            height={24}
                            fill={'#006192'}
                        />
                        <span className=" react-datepicker__navigation-icon--next">
                            {t('label.datepicker.next-month')}
                        </span>
                    </button>
                    {!isMonthPicker && (
                        <div className="react-datepicker-current-month-year" onClick={() => setIsMonthPicker(true)}>
                            {t('label.datepicker.select-month-and-year')}
                        </div>
                    )}
                    {!isYearPicker && isMonthPicker && (
                        <div
                            className="react-datepicker-current-month-year"
                            onClick={() => {
                                setIsYearPicker(true)
                                setUseCustomHeader(false)
                            }}>
                            {t('label.datepicker.select-year')}
                        </div>
                    )}
                </div>
            </div>
        </>
    )

    const customContainer = ({className, children}) => {
        return (
            <div className={'year-month-picker-container'}>
                {withCurrentDate ? (
                    <>
                        <div className="react-datepicker-btn flex">
                            <div
                                className={'btn-current' + (dateMode === 'current' ? ' active' : '')}
                                onClick={() => {
                                    handleChangeMode('current')
                                }}>
                                <span className="flex">{t('label.datepicker.title3')}</span>
                            </div>
                            <div
                                className={'btn-per-day' + (dateMode === 'forday' ? ' active' : '')}
                                onClick={() => {
                                    handleChangeMode('forday')
                                }}>
                                <span className="flex">{t('label.datepicker.title2')}</span>
                            </div>
                        </div>

                        {dateMode == 'current' && (
                            <>
                                <div className="info-box" style={{width: '280px'}}>
                                    <div className="info-box-border info-box-border-current flex">
                                        <span className="icon-info flex">
                                            <SVGIcon
                                                id={'icon-information'}
                                                className={''}
                                                width={24}
                                                height={24}
                                                fill={''}
                                            />
                                        </span>
                                        <span className="title flex" style={{padding: '0 16px 16px 0'}}>
                                            {t('label.infobox.current.quotes')}
                                        </span>
                                    </div>
                                </div>
                                <div className="react-datepicker-btn flex" style={{justifyContent: 'center'}}>
                                    <div className="btn-ok" onClick={handleChangeCurrentDate}>
                                        <span className="flex">{t('label.datepicker.understand')}</span>
                                    </div>
                                </div>
                            </>
                        )}

                        {dateMode == 'forday' && (
                            <CalendarContainer className={className}>
                                <div>{children}</div>
                            </CalendarContainer>
                        )}
                    </>
                ) : (
                    <CalendarContainer className={className}>
                        <div>{children}</div>
                    </CalendarContainer>
                )}
            </div>
        )
    }

    const CustomInput = forwardRef(({value, onClick, className}, ref) => {
        return (
            <button className={className} onClick={onClick} ref={ref}>
                {value === '' ? t('label.datepicker.title3') : value}
            </button>
        )
    })

    return (
        <DatePicker
            calendarStartDay={1}
            locale={language}
            dateFormat="dd.MM.yyyy"
            minDate={datePickerMinDate}
            maxDate={datePickerMaxDate}
            className={className}
            selected={selectedDate}
            onChange={handleDateChange}
            showIcon={true}
            icon="icon-calendar"
            ref={datepickerRef}
            calendarContainer={customContainer}
            renderCustomHeader={useCustomHeader ? customHeader : undefined}
            renderDayContents={renderDayContents}
            customInput={<CustomInput />}
            shouldCloseOnSelect={!isMonthPicker && !isYearPicker}
            showMonthYearPicker={isMonthPicker}
            showYearPicker={isYearPicker}
            yearItemNumber={20}
            filterDate={!isMonthPicker && !isYearPicker ? filterDate : undefined}
            excludeDates={!isMonthPicker && !isYearPicker ? excludeDates : []}
            formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 1)}
            onCalendarClose={() => {
                setIsMonthPicker(false)
                setIsYearPicker(false)
                setUseCustomHeader(true)
            }}
            onKeyDown={(e) => {
                e.preventDefault()
            }}
            onFocus={(e) => e.target.blur()}
        />
    )
}

export default YearMonthPicker
